import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { formatCurrency, getCurrencySymbol } from '@/utils/formatters';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, UserIcon, TrashIcon } from '@heroicons/react/24/outline';
import PaginationFooter from '@/components/PaginationFooter';
import { TagFilter } from '@/components/TagFilter';
import type { Tag } from '@/components/TagBadge';
import { useI18n } from '@/hooks/useI18n';

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone_number: string | null;
    tax_id: string | null;
    bank_account_number: string | null;
    user_id: string | null;
    manager_id: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string | null;
    contract_types: string[];
}

interface User {
    id: string;
    email: string;
    is_active: boolean;
}

interface EmployeeFormData {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    bank_account_number: string;
    manager_id: string;
    is_active: boolean;
}

// Contract-related types for combined create flow
interface CostCenter { id: string; name: string; }

interface Company {
    id: string;
    name: string;
    tax_id: string | null;
    is_active: boolean;
}

interface TimesheetJobLevel {
    id: string;
    job_id: string;
    level_name: 'Junior' | 'Experienced' | 'Senior';
    hourly_rate: number;
    is_default: boolean;
}

interface TimesheetJob {
    id: string;
    title: string;
    levels?: TimesheetJobLevel[];
}

interface ContractFormData {
    company_id: string;
    taxation_type: 'full_time_employee' | 'daily_employee' | 'student';
    default_cost_center_id: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
}

interface JobAssignmentFormData {
    job_id: string;
    job_level_id: string;
    cost_center_id: string;
}


export default function EmployeesPage() {
    const navigate = useNavigate();
    const { t } = useI18n();

    // State
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [allEmployees, setAllEmployees] = useState<Employee[]>([]); // For manager dropdown
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showInactiveUsers, setShowInactiveUsers] = useState(false);
    const [tagFilteredIds, setTagFilteredIds] = useState<string[] | null>(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    // Modal state for creating new employees
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState<EmployeeFormData>({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        bank_account_number: '',
        manager_id: '',
        is_active: true
    });
    const [submitting, setSubmitting] = useState(false);

    // Contract creation state (for one-step employee + contract)
    const [contractForm, setContractForm] = useState<ContractFormData>({
        company_id: '',
        taxation_type: 'full_time_employee',
        default_cost_center_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        is_active: true,
    });
    const [jobAssignments, setJobAssignments] = useState<JobAssignmentFormData[]>([]);

    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [jobs, setJobs] = useState<TimesheetJob[]>([]);
    const [baseCurrency, setBaseCurrency] = useState<string>('');


    // Helper functions
    const getManager = (managerId: string | null) => {
        if (!managerId) return null;
        return allEmployees.find(e => e.id === managerId);
    };


    // Data fetchers for contract-related dropdowns
    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching cost centers:', err);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await apiFetch('/api/companies/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setCompanies(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
        }
    };

    const fetchJobs = async () => {
        try {
            const response = await apiFetch('/api/timesheet/jobs?per_page=100&include_deleted=false');
            if (response.ok) {
                const data = await response.json();
                setJobs(data.jobs || []);
            }
        } catch (err) {
            console.error('Error fetching jobs:', err);
        }
    };

    const fetchBaseCurrency = async () => {
        try {
            const response = await apiFetch('/api/currencies/base-currency');
            if (response.ok) {
                const data = await response.json();
                setBaseCurrency(data.base_currency);
            } else {
                setBaseCurrency('HUF');
            }
        } catch (err) {
            console.error('Error fetching base currency:', err);
            setBaseCurrency('HUF');
        }
    };

    // Job assignment helpers (mirroring contracts page behavior)
    const addJobAssignment = () => {
        setJobAssignments(prev => ([...prev, { job_id: '', job_level_id: '', cost_center_id: '' }]));
    };

    const removeJobAssignment = (index: number) => {
        setJobAssignments(jobAssignments.filter((_, i) => i !== index));
    };

    const updateJobAssignment = (index: number, field: keyof JobAssignmentFormData, value: string) => {
        const updated = [...jobAssignments];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'job_id') updated[index].job_level_id = '';
        setJobAssignments(updated);
    };

    const getAvailableLevelsForJob = (jobId: string): TimesheetJobLevel[] => {
        const job = jobs.find(j => j.id === jobId);
        return job?.levels || [];
    };

    const validateContractForm = (): string | null => {
        if (!contractForm.company_id) return t('employees.validation.companyRequired', 'people');
        if (!contractForm.start_date) return t('employees.validation.contractStartDateRequired', 'people');
        if (jobAssignments.length === 0) return t('employees.validation.atLeastOneJobAssignment', 'people');
        for (let i = 0; i < jobAssignments.length; i++) {
            const assignment = jobAssignments[i];
            if (!assignment.job_id) return `${t('employees.jobAssignment', 'people')} ${i + 1}: ${t('employees.validation.pleaseSelectJob', 'people')}`;
            if (!assignment.job_level_id) return `${t('employees.jobAssignment', 'people')} ${i + 1}: ${t('employees.validation.pleaseSelectLevel', 'people')}`;
            const duplicateJob = jobAssignments.find((ja, idx) => idx !== i && ja.job_id === assignment.job_id);
            if (duplicateJob) return t('employees.validation.eachJobOnce', 'people');
        }
        return null;
    };

    // Fetch data
    useEffect(() => {
        fetchEmployees();
        fetchAllEmployees();
    }, [pagination.page, pagination.limit, searchTerm, showInactiveUsers]);

    const fetchAllEmployees = async () => {
        try {
            // Fetch all active employees without pagination for manager dropdown
            const response = await apiFetch('/api/people/employees/?limit=100&is_active=true');
            if (response.ok) {
                const data = await response.json();
                setAllEmployees(data.employees);
            }
        } catch (err) {
            console.error('Error fetching all employees:', err);
        }
    };

    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const skip = (pagination.page - 1) * pagination.limit;
            const params = new URLSearchParams({
                skip: skip.toString(),
                limit: pagination.limit.toString(),
                ...(searchTerm && { search: searchTerm }),
                ...(!showInactiveUsers && { is_active: 'true' })
            });

            const response = await apiFetch(`/api/people/employees/?${params}`);
            if (response.ok) {
                const data = await response.json();
                setEmployees(data.employees);
                setPagination(prev => ({
                    ...prev,
                    total: data.total,
                    pages: data.pagination.pages
                }));
            } else {
                throw new Error('Failed to fetch employees');
            }
        } catch (err) {
            console.error('Error fetching employees:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch employees');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleRowClick = (employeeId: string) => {
        navigate({ to: `/people/employees/${employeeId}` });
    };

    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            bank_account_number: '',
            manager_id: '',
            is_active: true
        });
    };

    const openCreateModal = () => {
        resetForm();
        setContractForm({
            company_id: '',
            taxation_type: 'full_time_employee',
            default_cost_center_id: '',
            start_date: new Date().toISOString().split('T')[0],
            end_date: '',
            is_active: true,
        });
        setJobAssignments([]);
        // Fetch supporting data for contract section
        fetchCostCenters();
        fetchCompanies();
        fetchJobs();
        fetchBaseCurrency();
        setShowCreateModal(true);
    };



    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        // Validate contract fields (since we're creating both together)
        const validationError = validateContractForm();
        if (validationError) {
            setError(validationError);
            setSubmitting(false);
            return;
        }

        try {
            // 1) Create employee
            const employeePayload = {
                ...formData,
                email: formData.email || null,
                phone_number: formData.phone_number || null,
                bank_account_number: formData.bank_account_number || null,
                manager_id: formData.manager_id || null
            };

            const empRes = await apiFetch('/api/people/employees/?skip_auto_contract=true', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(employeePayload)
            });

            if (!empRes.ok) {
                const errorData = await empRes.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to create employee');
            }
            const newEmployee = await empRes.json();

            // 2) Create initial contract for the new employee
            const contractPayload = {
                employee_id: newEmployee.id,
                company_id: contractForm.company_id,
                taxation_type: contractForm.taxation_type,
                default_cost_center_id: contractForm.default_cost_center_id || null,
                start_date: new Date(contractForm.start_date).toISOString(),
                end_date: contractForm.end_date ? new Date(contractForm.end_date).toISOString() : null,
                is_active: contractForm.is_active,
                job_assignments: jobAssignments.map(ja => ({
                    job_id: ja.job_id,
                    job_level_id: ja.job_level_id,
                    cost_center_id: ja.cost_center_id || null,
                })),
            };

            const contractRes = await apiFetch('/api/people/contracts/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(contractPayload)
            });

            if (!contractRes.ok) {
                const errorData = await contractRes.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Employee created but failed to create contract');
            }

            await fetchEmployees();
            await fetchAllEmployees();
            setShowCreateModal(false);
            resetForm();
            setContractForm({
                company_id: '',
                taxation_type: 'full_time_employee',
                default_cost_center_id: '',
                start_date: new Date().toISOString().split('T')[0],
                end_date: '',
                is_active: true,
            });
            setJobAssignments([]);
        } catch (err) {
            console.error('Error creating employee and contract:', err);
            setError(err instanceof Error ? err.message : 'Failed to create employee and contract');
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('navigation.employees', 'common')}</h1>
                        <p className="text-muted-foreground">
                            {t('common.subtitle', 'people')}
                        </p>
                    </div>
                    <Button onClick={openCreateModal}>
                        <PlusIcon className="w-4 h-4 mr-2" />
                        {t('actions.addNew', 'common')} {t('common.employee', 'people')}
                    </Button>
                </div>

                {/* Search and Filters */}
                <div className="flex gap-4">
                    <Input
                        placeholder={t('common.search', 'people')}
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="max-w-sm"
                    />
                    <label className="flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground">
                        <input
                            type="checkbox"
                            checked={showInactiveUsers}
                            onChange={(e) => {
                                setShowInactiveUsers(e.target.checked);
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                            className="rounded border-zinc-300"
                        />
                        {t('employees.showInactiveUsers', 'people')}
                    </label>
                    <TagFilter
                        entityType="employees"
                        onFilterChange={(selection) => {
                            setTagFilteredIds(selection ? selection.tagIds : null);
                        }}
                    />
                </div>

                {/* Error Display */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}




                {/* Table */}
                <div className="border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('common.name', 'people')}</TableHead>
                                <TableHead>{t('common.email', 'people')}</TableHead>
                                <TableHead>{t('common.phone', 'people')}</TableHead>
                                <TableHead>{t('common.contractType', 'people')}</TableHead>
                                <TableHead>{t('common.manager', 'people')}</TableHead>
                                <TableHead>{t('common.status', 'people')}</TableHead>

                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                    </TableRow>
                                ))
                            ) : employees.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8">
                                        <div className="flex flex-col items-center gap-2">
                                            <UserIcon className="h-12 w-12 text-gray-400" />
                                            <p className="text-muted-foreground">{t('employees.noEmployees', 'people')}</p>
                                            <Button variant="outline" onClick={openCreateModal}>
                                                {t('employees.createFirst', 'people')}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                employees
                                    .filter((employee) => !tagFilteredIds || tagFilteredIds.includes(employee.id))
                                    .map((employee) => (
                                        <TableRow
                                            key={employee.id}
                                            className={`${!employee.is_active ? 'opacity-60 bg-muted/20' : ''} cursor-pointer hover:bg-muted/50`}
                                            onClick={() => handleRowClick(employee.id)}
                                        >
                                            <TableCell className="font-medium">
                                                {employee.first_name} {employee.last_name}
                                            </TableCell>
                                            <TableCell>
                                                {employee.email || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {employee.phone_number || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {employee.contract_types && employee.contract_types.length > 0 ? (
                                                    employee.contract_types.map(ct => {
                                                        if (ct === 'full_time_employee') return t('employees.fullTime', 'people');
                                                        if (ct === 'daily_employee') return t('employees.daily', 'people');
                                                        if (ct === 'student') return t('employees.student', 'people');
                                                        return ct;
                                                    }).join(', ')
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {getManager(employee.manager_id) ? (
                                                    <span>{getManager(employee.manager_id)!.first_name} {getManager(employee.manager_id)!.last_name}</span>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={employee.is_active ? "default" : "secondary"}>
                                                    {employee.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                <PaginationFooter
                    pagination={pagination}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                    onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }))}
                />
            </div>

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateModal(false);
                    resetForm();
                }
            }}>
                <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{t('employees.createEmployee', 'people')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="first_name">{t('forms.firstName', 'common')} *</Label>
                                <Input
                                    id="first_name"
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="last_name">{t('forms.lastName', 'common')} *</Label>
                                <Input
                                    id="last_name"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="email">{t('common.email', 'people')}</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>

                        <div>
                            <Label htmlFor="phone_number">{t('forms.phoneNumber', 'common')}</Label>
                            <Input
                                id="phone_number"
                                value={formData.phone_number}
                                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                            />
                        </div>

                        <div>
                            <Label htmlFor="bank_account_number">{t('employees.bankAccount', 'people')}</Label>
                            <Input
                                id="bank_account_number"
                                value={formData.bank_account_number}
                                onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                            />
                        </div>

                        <div>
                            <Label htmlFor="manager_id">{t('employees.manager', 'people')} ({t('common.optional', 'common')})</Label>
                            <Select value={formData.manager_id || "none"} onValueChange={(value) => setFormData({ ...formData, manager_id: value === "none" ? "" : value })}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('employees.selectManager', 'people')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t('employees.noManager', 'people')}</SelectItem>
                                    {allEmployees.map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id}>
                                            {emp.first_name} {emp.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* User account is now created automatically - no need for selector */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <p className="text-sm text-blue-800">
                                <strong>{t('common.note', 'common')}:</strong> {t('employees.autoUserCreationNote', 'people')}
                            </p>
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="rounded"
                            />
                            <Label htmlFor="is_active">{t('common.active', 'common')}</Label>
                        </div>


                        {/* Contract details */}
                        <div className="border-t pt-4 space-y-4">
                            <div>
                                <h3 className="text-lg font-semibold">{t('employees.contractDetails', 'people')}</h3>
                                <p className="text-sm text-muted-foreground">{t('employees.contractDetailsSubtitle', 'people')}</p>
                            </div>

                            {/* Inline error for contract validation */}
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                                    {error}
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="company_id">{t('employees.company', 'people')} *</Label>
                                    <Select
                                        value={contractForm.company_id}
                                        onValueChange={(value) =>
                                            setContractForm({ ...contractForm, company_id: value })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('employees.selectCompany', 'people')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {companies.map((company) => (
                                                <SelectItem key={company.id} value={company.id}>
                                                    {company.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label htmlFor="taxation_type">{t('employees.taxationType', 'people')} *</Label>
                                    <Select
                                        value={contractForm.taxation_type}
                                        onValueChange={(value: 'full_time_employee' | 'daily_employee' | 'student') =>
                                            setContractForm({ ...contractForm, taxation_type: value })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="full_time_employee">{t('employees.fullTime', 'people')}</SelectItem>
                                            <SelectItem value="daily_employee">{t('employees.daily', 'people')}</SelectItem>
                                            <SelectItem value="student">{t('employees.student', 'people')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label htmlFor="default_cost_center_id">{t('employees.defaultCostCenter', 'people')}</Label>
                                    <Select
                                        value={contractForm.default_cost_center_id || 'none'}
                                        onValueChange={(value) =>
                                            setContractForm({ ...contractForm, default_cost_center_id: value === 'none' ? '' : value })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('employees.selectCostCenter', 'people')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">{t('employees.noDefaultCostCenter', 'people')}</SelectItem>
                                            {costCenters.map((cc) => (
                                                <SelectItem key={cc.id} value={cc.id}>
                                                    {cc.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label htmlFor="start_date">{t('employees.startDate', 'people')} *</Label>
                                    <Input
                                        id="start_date"
                                        type="date"
                                        value={contractForm.start_date}
                                        onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })}
                                        required
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="end_date">{t('employees.endDate', 'people')} ({t('common.optional', 'common')})</Label>
                                    <Input
                                        id="end_date"
                                        type="date"
                                        value={contractForm.end_date}
                                        onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })}
                                    />
                                </div>

                                <div className="flex items-center space-x-2 pt-6">
                                    <input
                                        type="checkbox"
                                        id="contract_is_active"
                                        checked={contractForm.is_active}
                                        onChange={(e) => setContractForm({ ...contractForm, is_active: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="contract_is_active">{t('employees.contractActive', 'people')}</Label>
                                </div>
                            </div>

                            {/* Job Assignments */}
                            <div className="border-t pt-4">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h4 className="text-md font-semibold">{t('contracts.jobAssignments', 'people')} *</h4>
                                        <p className="text-sm text-muted-foreground">{t('contracts.jobAssignmentsSubtitle', 'people')}</p>
                                    </div>
                                    <Button type="button" variant="outline" size="sm" onClick={addJobAssignment}>
                                        <PlusIcon className="w-4 h-4 mr-1" /> {t('contracts.addJob', 'people')}
                                    </Button>
                                </div>

                                {jobAssignments.length === 0 ? (
                                    <div className="text-center py-6 border-2 border-dashed rounded-lg">
                                        <p className="text-muted-foreground mb-2">{t('contracts.noJobAssignmentsYet', 'people')}</p>
                                        <Button type="button" variant="outline" size="sm" onClick={addJobAssignment}>
                                            <PlusIcon className="w-4 h-4 mr-1" /> {t('contracts.addFirstJob', 'people')}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {jobAssignments.map((assignment, index) => {
                                            const availableLevels = getAvailableLevelsForJob(assignment.job_id);
                                            return (
                                                <div key={index} className="flex gap-2 items-start p-3 border rounded-lg bg-muted/20">
                                                    <div className="flex-1 grid grid-cols-3 gap-2">
                                                        <div>
                                                            <Label className="text-xs">{t('employees.job', 'people')} *</Label>
                                                            <Select
                                                                value={assignment.job_id}
                                                                onValueChange={(value) => updateJobAssignment(index, 'job_id', value)}
                                                            >
                                                                <SelectTrigger className="h-9">
                                                                    <SelectValue placeholder={t('employees.selectJob', 'people')} />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {jobs.map((job) => (
                                                                        <SelectItem key={job.id} value={job.id}>
                                                                            {job.title}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">{t('employees.level', 'people')} *</Label>
                                                            <Select
                                                                value={assignment.job_level_id}
                                                                onValueChange={(value) => updateJobAssignment(index, 'job_level_id', value)}
                                                                disabled={!assignment.job_id}
                                                            >
                                                                <SelectTrigger className="h-9">
                                                                    <SelectValue placeholder={t('employees.selectLevel', 'people')} />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {availableLevels.map((level) => (
                                                                        <SelectItem key={level.id} value={level.id}>
                                                                            {level.level_name} ({formatCurrency(level.hourly_rate, getCurrencySymbol(baseCurrency))}/hr)
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">{t('employees.costCenterOverride', 'people')}</Label>
                                                            <Select
                                                                value={assignment.cost_center_id || 'none'}
                                                                onValueChange={(value) => updateJobAssignment(index, 'cost_center_id', value === 'none' ? '' : value)}
                                                            >
                                                                <SelectTrigger className="h-9">
                                                                    <SelectValue placeholder={t('employees.useDefault', 'people')} />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="none">{t('employees.useDefault', 'people')}</SelectItem>
                                                                    {costCenters.map((cc) => (
                                                                        <SelectItem key={cc.id} value={cc.id}>
                                                                            {cc.name}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                    <Button type="button" variant="ghost" size="sm" onClick={() => removeJobAssignment(index)} className="mt-5">
                                                        <TrashIcon className="w-4 h-4 text-red-500" />
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                            >
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting ? t('actions.creating', 'common') : t('actions.create', 'common')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

export const Route = createFileRoute('/people/employees/')({
    component: EmployeesPage,
})
